// Copyright (c) Docugami, Inc. All rights reserved.

import { emptyGray } from 'components/shell/presentation/AppStyles'
import styled, { css } from 'styled-components'
import { ExplorerViewType } from './ExplorerTypes'
import { IBadgeProps } from './Badge'
import { NavigationItem, NavigationItemClassNames } from './NavigationItem'
import { BasicIconProps } from 'components/work_path/IconRenderer'

export const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const NavItemContainer = styled.div<{divider: boolean}>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${props => props.divider && dividerCSS};
`;

const dividerCSS = css`
    border-bottom: 1px solid ${emptyGray};
    padding-bottom: 6px;
    box-sizing: border-box;
`;

export interface NavigationListItem {
    id: string;
    title: string;
    type: ExplorerViewType;
    badgeProps?: IBadgeProps;
    icon?: BasicIconProps;
    hasDivider?: boolean;
    disabled?: boolean;
    onClick: () => void;
    automationId: string;
    styles?: NavigationItemClassNames[];
}

export interface NavigationItemListProps {
    items: NavigationListItem[];
    selectedId?: string;
}

export const NavigationItemList = (props: NavigationItemListProps): JSX.Element => {
    function handleItemList() {
        if (props.items.length === 0) {
            return [];
        }

        const items = props.items.reduce((result, item) => {
            if (!result[item.type]) {
                result[item.type] = [item];
            }
            else {
                result[item.type].push(item);
            }
            return result;
        }, {} as Record<ExplorerViewType, NavigationListItem[]>);

        return Object.values(items).flatMap(list => {
            const itemList = list.map(item => {
                const listItem = (
                    <NavItemContainer key={item.id} data-key={item.title} divider={!!item.hasDivider}>
                        <NavigationItem
                            automationId={item.automationId}
                            title={item.title}
                            isSelected={props.selectedId === item.id}
                            onSelected={item.onClick}
                            badgeProps={item.badgeProps}
                            icon={item.icon}
                            disabled={item.disabled}
                            styles={item.styles}
                        />
                    </NavItemContainer>
                );
                return listItem;
            });
            return itemList;
        })
    }

    return (
        <ListContainer>
            {handleItemList()}
        </ListContainer>
    )
}
